// A person or group's picture, or an initials fallback when there is none. The fallback's colour is
// picked from the palette's hue wheel (the categorical swatch set), the same string always getting
// the same one. The presence dot is decoration: its text is the consuming row's job.
import "./config";
import { Text, View } from "tamagui";
import { Icon, type IconName } from "./Icon";
import { Picture } from "./surfaces";
import type { PaletteKey } from "./palette";

export type AvatarSize = "row" | "detail" | "inline";
export type AvatarPresence = "online" | "offline" | "scheduled" | "tasting";

export interface AvatarProps {
  /** Precomputed initials (1-2 characters); the caller derives these from a person or group's
   * name, Avatar only renders and colours them. */
  initials: string;
  src?: string | undefined;
  size?: AvatarSize;
  presence?: AvatarPresence | undefined;
  /** Shown instead of initials while there is no picture (an empty "add a picture" slot). */
  icon?: IconName | undefined;
}

const HUES = [
  "hueLemonGreen",
  "hueLemon",
  "hueGold",
  "hueAmber",
  "hueBrown",
  "huePink",
  "hueSalmon",
  "hueOrange",
  "huePurple",
  "hueRuby",
  "hueGarnet",
  "hueTawny",
] as const satisfies readonly PaletteKey[];

const SIZES = {
  row: { box: 32, font: "2" },
  detail: { box: 56, font: "6" },
  inline: { box: 20, font: "0" },
} as const;

const PRESENCE = {
  online: "ready",
  offline: "textFaint",
  scheduled: "wait",
  tasting: "accent",
} as const;

/** Same string -> same swatch, every render, every session: a sum of char codes is enough for a 12-way pick. */
function hueFor(seed: string): (typeof HUES)[number] {
  let sum = 0;
  for (let i = 0; i < seed.length; i += 1) sum += seed.charCodeAt(i);
  return HUES[sum % HUES.length] ?? HUES[0];
}

export function Avatar({
  initials,
  src,
  size = "row",
  presence,
  icon,
}: AvatarProps): React.JSX.Element {
  const { box, font } = SIZES[size];
  return (
    <View
      data-size={size}
      position="relative"
      flexShrink={0}
      width={box}
      height={box}
      borderRadius="pill"
      alignItems="center"
      justifyContent="center"
      backgroundColor={src === undefined && icon === undefined ? hueFor(initials) : "surface2"}
    >
      <View
        width="100%"
        height="100%"
        borderRadius="pill"
        overflow="hidden"
        alignItems="center"
        justifyContent="center"
      >
        {src === undefined && icon !== undefined ? (
          <Icon name={icon} size={Math.round(box / 2.8)} tone="textDim" />
        ) : src === undefined ? (
          <Text
            fontFamily="body"
            fontSize={font}
            fontWeight="700"
            lineHeight={font}
            color="accentInk"
            textTransform="uppercase"
          >
            {initials}
          </Text>
        ) : (
          <Picture src={src} alt="" width="100%" height="100%" objectFit="cover" />
        )}
      </View>
      {presence === undefined ? null : (
        <View
          data-presence={presence}
          position="absolute"
          right={-1}
          bottom={-1}
          width="30%"
          height="30%"
          minWidth={8}
          minHeight={8}
          borderRadius="pill"
          borderWidth={2}
          borderColor="surface"
          backgroundColor={PRESENCE[presence]}
          transitionProperty="background-color"
          transitionDuration="var(--durBase)"
          aria-hidden
        />
      )}
    </View>
  );
}
